"use client";

import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";

/** Extra course images as a slider: 3 at a time on desktop (2 tablet, 1 phone) with prev/next arrows. */
export function CourseGallery({ images, title }: { images: string[]; title: string }) {
  const swiperRef = useRef<SwiperType | null>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const sync = (s: SwiperType) => {
    setAtStart(s.isBeginning);
    setAtEnd(s.isEnd);
  };

  const arrow =
    "absolute top-1/2 z-10 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-line bg-white/95 text-ink shadow-soft backdrop-blur transition-colors hover:text-brand-700 disabled:pointer-events-none disabled:opacity-0";

  return (
    <div className="relative w-full min-w-0 max-w-full">
      <Swiper
        onSwiper={(s) => {
          swiperRef.current = s;
          sync(s);
        }}
        onSlideChange={sync}
        onResize={sync}
        onBreakpoint={sync}
        slidesPerView={1}
        spaceBetween={16}
        breakpoints={{
          640: { slidesPerView: 2, spaceBetween: 16 },
          1024: { slidesPerView: 3, spaceBetween: 20 },
        }}
        className="w-full"
      >
        {images.map((src, i) => (
          // Width set in CSS too so the server-rendered page already shows 1/2/3 per row, not one huge slide, before Swiper starts.
          <SwiperSlide key={`${src}-${i}`} className="!mr-4 !w-full sm:!w-[calc((100%-16px)/2)] lg:!mr-5 lg:!w-[calc((100%-40px)/3)]">
            <div className="overflow-hidden rounded-2xl border border-line bg-white shadow-soft">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={src}
                alt={`${title} - image ${i + 1}`}
                loading={i < 3 ? "eager" : "lazy"}
                className="aspect-[400/250] w-full object-cover"
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      <button type="button" aria-label="Previous images" disabled={atStart} onClick={() => swiperRef.current?.slidePrev()} className={`${arrow} left-2 sm:-left-3`}>
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button type="button" aria-label="Next images" disabled={atEnd} onClick={() => swiperRef.current?.slideNext()} className={`${arrow} right-2 sm:-right-3`}>
        <ChevronRight className="h-5 w-5" />
      </button>
    </div>
  );
}
